<template>
  <v-dialog
      v-model="dialog"
      :width="dialogWidth"
      @keydown.enter.prevent="onenter"
  >
    <v-card
        class="resizable-card"
        :class="{ 'no-select': resizing }"
        :style="{ height: dialogHeight + 'px' }"
    >
      <v-card-title>
        <span class="headline">Item Order ({{ rules.name }})</span>
        <v-spacer/>
        <r-authorize
            :master="masterAccess"
            :locked.sync="model.locked"
            :enabled.sync="model.enabled"
        />
      </v-card-title>
      <v-card-text class="resizable-card__body">
        <v-row dense>
          <v-col cols="12" md="12" sm="12">
            <v-btn @click="showPreview()" class="mr-2" small>View JSON</v-btn>
          </v-col>
        </v-row>
        <v-row dense class="mt-3">
          <v-col cols="12" md="12" sm="12">
            <v-text-field disabled label="UID:" v-model.trim="model.uid" hide-details outlined dense/>
          </v-col>
          <v-col cols="12" md="6" sm="6">
            <v-text-field
                label="Name:"
                v-model.trim="model.name"
                v-ascii-only
            />
          </v-col>
          <v-col cols="12" md="6" sm="6">
            <v-text-field
                label="Key:"
                v-model.trim="model.key"
                v-ascii-only
            />
          </v-col>
        </v-row>
        <v-row dense class="mb-4">
          <v-col cols="12" sm="6" md="6">
            <r-item-order-kind :model.sync="model.kind" label="Kind"/>
          </v-col>
        </v-row>
        <v-expansion-panels>
          <r-guard-panel :model.sync="model.guard"/>
          <r-money-panel :model.sync="model.price" title="Price" :ticket="false"/>
          <r-reward-panel :model.sync="model.reward" title="Rewards"/>
        </v-expansion-panels>
      </v-card-text>
      <v-card-actions class="resizable-card__actions">
        <v-spacer/>
        <v-btn @click="cancel" color="blue darken-1" text>
          Cancel
        </v-btn>
        <v-btn @click="save" color="blue darken-1" text v-if="!lockedAssess || masterAccess">
          Save
        </v-btn>
      </v-card-actions>
      <div class="resize-handle" @mousedown="startResize"></div>
      <r-preview-dialog ref="preview-dialog"></r-preview-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import VueTypes from "vue-types";
import {ViewRules} from "@/common/ViewRules";
import {ItemOrder} from "@/modules/redactor/models/item-order/ItemOrder";
import {RedactorUtils} from "@/modules/redactor/core/RedactorUtils";

import RAuthorize from "@/modules/redactor/views/common/r-authorize";
import RGuardPanel from "@/modules/redactor/views/guard/r-guard-panel";
import RRewardPanel from "@/modules/redactor/views/reward/r-reward-panel.vue";
import RMoneyPanel from "@/modules/redactor/views/currency/r-money-panel.vue";
import RItemOrderKind from "@/modules/redactor/views/item-order/r-item-order-kind.vue";
import RPreviewDialog from "@/modules/redactor/views/preview/r-preview-dialog.vue";

export default {
  name: "r-item-order-dialog",
  components: {RPreviewDialog, RItemOrderKind, RMoneyPanel, RRewardPanel, RAuthorize, RGuardPanel},
  data: () => ({
    dialog: false,
    resolve: null,
    reject: null,
    model: VueTypes.objectOf(ItemOrder),
    rules: ViewRules.READONLY,
    dialogWidth: 575,
    dialogHeight: 546,
    resizing: false,
    resizeStartX: 0,
    resizeStartY: 0,
    resizeStartWidth: 575,
    resizeStartHeight: 546,
    viewportPadding: 32
  }),
  computed: {
    lockedAssess() {
      return this.model.locked;
    },
    masterAccess() {
      return RedactorUtils.hasMasterAccess();
    }
  },
  methods: {
    show(rules, model) {
      this.dialog = true;
      this.rules = rules;
      this.model = model || new ItemOrder();

      if (ViewRules.COPY.equal(this.rules)) {
        this.model.locked = false;
      }

      return new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      });
    },
    save() {
      if (this.model.valid()) {
        this.resolve && this.resolve(this.model);
        this.discard();
      } else {
        this.$alert("Invalid config!");
      }
    },
    cancel() {
      this.reject && this.reject();
      this.discard();
    },
    discard() {
      this.dialog = false;
      this.stopResize();
    },
    onenter() {
      if (!this.lockedAssess || this.masterAccess) {
        this.save();
      } else {
        this.cancel();
      }
    },
    startResize(e) {
      this.resizing = true;
      this.resizeStartX = e.clientX;
      this.resizeStartY = e.clientY;
      this.resizeStartWidth = this.dialogWidth;
      this.resizeStartHeight = this.dialogHeight;
      window.addEventListener("mousemove", this.onResize);
      window.addEventListener("mouseup", this.stopResize);
    },
    onResize(e) {
      if (!this.resizing) return;
      const dx = e.clientX - this.resizeStartX;
      const dy = e.clientY - this.resizeStartY;
      let newWidth = this.resizeStartWidth + dx;
      let newHeight = this.resizeStartHeight + dy;
      const minWidth = 420;
      const minHeight = 320;
      if (newWidth < minWidth) newWidth = minWidth;
      if (newHeight < minHeight) newHeight = minHeight;
      this.dialogWidth = newWidth;
      this.dialogHeight = newHeight;
    },
    stopResize() {
      if (!this.resizing) return;
      this.resizing = false;
      window.removeEventListener("mousemove", this.onResize);
      window.removeEventListener("mouseup", this.stopResize);
    },
    showPreview() {
      this.$refs['preview-dialog'].show(this.rules, this.model, ItemOrder)
          .then((result) => {
            Object.assign(this.model, result);
          })
          .catch((e) => {
            this.$alert("Failed to parse config!" + e.message);
          });
    },
  },
  beforeDestroy() {
    window.removeEventListener("mousemove", this.onResize);
    window.removeEventListener("mouseup", this.stopResize);
  }
};
</script>

<style scoped>
.resizable-card {
  position: relative;
  display: flex;
  flex-direction: column;
}
.resizable-card__body {
  flex: 1;
  overflow: auto;
}
.resizable-card__actions {
  flex-shrink: 0;
}
.resize-handle {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 14px;
  height: 14px;
  cursor: se-resize;
  border-right: 2px solid rgba(0, 0, 0, 0.3);
  border-bottom: 2px solid rgba(0, 0, 0, 0.3);
}
.no-select {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}
</style>